import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

class ProcessService {
  /**
   * Get all processes with optional filters
   * @param {Object} options - Query options
   * @returns {Promise<Object>} API response
   */
  async getAllProcesses(options = {}) {
    const {
      process_type,
      status,
      limit = 50,
      offset = 0,
      order_by = 'created_at',
      order_direction = 'DESC'
    } = options;

    const params = new URLSearchParams();
    if (process_type) params.append('process_type', process_type);
    if (status) params.append('status', status);
    if (limit) params.append('limit', limit);
    if (offset) params.append('offset', offset);
    if (order_by) params.append('order_by', order_by);
    if (order_direction) params.append('order_direction', order_direction);

    const response = await axios.get(`${API_BASE_URL}/backend/processes/all?${params}`);
    return response.data;
  }

  /**
   * Get only active processes
   * @returns {Promise<Object>} API response
   */
  async getActiveProcesses() {
    const response = await axios.get(`${API_BASE_URL}/backend/processes/active`);
    return response.data;
  }

  /**
   * Get process statistics
   * @param {string} timeframe - Time frame for statistics
   * @returns {Promise<Object>} API response
   */
  async getProcessStatistics(timeframe = '24h') {
    const response = await axios.get(`${API_BASE_URL}/backend/processes/statistics?timeframe=${timeframe}`);
    return response.data;
  }

  /**
   * Get specific process details
   * @param {string} processId - Process ID
   * @returns {Promise<Object>} API response
   */
  async getProcess(processId) {
    const response = await axios.get(`${API_BASE_URL}/backend/processes/${processId}`);
    return response.data;
  }

  /**
   * Cancel a process
   * @param {string} processId - Process ID
   * @returns {Promise<Object>} API response
   */
  async cancelProcess(processId) {
    const response = await axios.post(`${API_BASE_URL}/backend/processes/${processId}/cancel`);
    return response.data;
  }

  /**
   * Cleanup old and stuck processes
   * @param {Object} options - Cleanup options
   * @returns {Promise<Object>} API response
   */
  async cleanupProcesses(options = {}) {
    const {
      max_age_hours = 24,
      mark_stuck_as_failed = true,
      stuck_threshold_minutes = 30
    } = options;

    const response = await axios.post(`${API_BASE_URL}/backend/processes/cleanup`, {
      max_age_hours,
      mark_stuck_as_failed,
      stuck_threshold_minutes
    });
    return response.data;
  }

  /**
   * Get process type display name
   * @param {string} processType - Process type
   * @returns {string} Display name
   */
  getProcessTypeDisplayName(processType) {
    const displayNames = {
      'BASIC_INFO': 'Basic Info',
      'GROUP_JOIN': 'Group Join',
      'JOIN_GROUPS': 'Group Join',
      'AI_ANALYSIS': 'AI Analysis',
      'ACCOUNT_SCAN': 'Account Scan',
      'UNSUBSCRIBE': 'Unsubscribe',
      'ADD_GROUPS': 'Add Groups',
      'AUTO_ASSIGNMENT': 'Auto Assignment'
    };
    return displayNames[processType] || processType;
  }

  /**
   * Get status display info
   * @param {string} status - Process status
   * @returns {Object} Status display info
   */
  getStatusDisplayInfo(status) {
    const statusInfo = {
      'pending': {
        color: 'yellow',
        icon: '⏳',
        text: 'Pending'
      },
      'in_progress': {
        color: 'blue',
        icon: '🔄',
        text: 'In Progress'
      },
      'completed': {
        color: 'green',
        icon: '✅',
        text: 'Completed'
      },
      'failed': {
        color: 'red',
        icon: '❌',
        text: 'Failed'
      },
      'cancelled': {
        color: 'gray',
        icon: '🚫',
        text: 'Cancelled'
      },
      'paused': {
        color: 'orange',
        icon: '⏸️',
        text: 'Paused'
      }
    };
    return statusInfo[status] || {
      color: 'gray',
      icon: '?',
      text: status
    };
  }

  /**
   * Format duration in seconds to human readable format
   * @param {number} seconds - Duration in seconds
   * @returns {string} Formatted duration
   */
  formatDuration(seconds) {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  }

  /**
   * Get process type icon
   * @param {string} processType - Process type
   * @returns {string} Icon
   */
  getProcessTypeIcon(processType) {
    const icons = {
      'BASIC_INFO': '📊',
      'GROUP_JOIN': '👥',
      'JOIN_GROUPS': '👥',
      'AI_ANALYSIS': '🤖',
      'ACCOUNT_SCAN': '🔍',
      'UNSUBSCRIBE': '🚪',
      'ADD_GROUPS': '➕',
      'AUTO_ASSIGNMENT': '🎯'
    };
    return icons[processType] || '⚙️';
  }

  /**
   * Calculate progress percentage
   * @param {Object} progress - Progress object
   * @returns {number} Percentage
   */
  calculateProgressPercentage(progress) {
    if (!progress || progress.total === 0) return 0;
    return Math.round((progress.processed / progress.total) * 100);
  }

  /**
   * Get progress color based on percentage
   * @param {number} percentage - Progress percentage
   * @returns {string} Color class
   */
  getProgressColor(percentage) {
    if (percentage >= 90) return 'green';
    if (percentage >= 70) return 'blue';
    if (percentage >= 50) return 'yellow';
    return 'red';
  }

  /**
   * Check if process is active
   * @param {string} status - Process status
   * @returns {boolean} Is active
   */
  isProcessActive(status) {
    return ['pending', 'in_progress', 'paused'].includes(status);
  }

  /**
   * Check if process can be cancelled
   * @param {string} status - Process status
   * @returns {boolean} Can be cancelled
   */
  canCancelProcess(status) {
    return ['pending', 'in_progress', 'paused'].includes(status);
  }

  /**
   * Get time ago string
   * @param {string} timestamp - ISO timestamp
   * @returns {string} Time ago string
   */
  getTimeAgo(timestamp) {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 60) {
      return `${diffInSeconds}s ago`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    }
  }

  /**
   * Format timestamp to local string
   * @param {string} timestamp - ISO timestamp
   * @returns {string} Formatted timestamp
   */
  formatTimestamp(timestamp) {
    return new Date(timestamp).toLocaleString();
  }

  /**
   * Get process color based on status
   * @param {string} status - Process status
   * @returns {string} Color class
   */
  getProcessColor(status) {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'in_progress': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'failed': 'bg-red-100 text-red-800',
      'cancelled': 'bg-gray-100 text-gray-800',
      'paused': 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }

  /**
   * Extract last log message
   * @param {Array} logs - Process logs
   * @returns {string} Last log message
   */
  getLastLogMessage(logs) {
    if (!logs || logs.length === 0) return 'No logs available';
    const lastLog = logs[logs.length - 1];
    return lastLog.message || 'No message';
  }

  /**
   * Get process success rate
   * @param {Object} progress - Progress object
   * @returns {number} Success rate percentage
   */
  getSuccessRate(progress) {
    if (!progress || progress.processed === 0) return 0;
    return Math.round((progress.successful / progress.processed) * 100);
  }

  /**
   * Get process failure rate
   * @param {Object} progress - Progress object
   * @returns {number} Failure rate percentage
   */
  getFailureRate(progress) {
    if (!progress || progress.processed === 0) return 0;
    return Math.round((progress.failed / progress.processed) * 100);
  }

  /**
   * Check if process is stuck (no updates for long time)
   * @param {string} updatedAt - Last updated timestamp
   * @param {number} thresholdMinutes - Threshold in minutes
   * @returns {boolean} Is stuck
   */
  isProcessStuck(updatedAt, thresholdMinutes = 30) {
    const now = new Date();
    const lastUpdate = new Date(updatedAt);
    const diffInMinutes = Math.floor((now - lastUpdate) / (1000 * 60));
    return diffInMinutes > thresholdMinutes;
  }
}

export default new ProcessService();
